import { useNavigate, useSearchParams } from 'react-router-dom'
import InputAdornment from '@mui/material/InputAdornment'
import OutlinedInput from '@mui/material/OutlinedInput'
import { CircularProgress, useMediaQuery, useTheme } from '@mui/material'
import { Search } from '@mui/icons-material'
import React, { useState, useEffect } from 'react'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'

import { UnAuthorizedVersion as LanguagePopover } from 'shared/components/LanguagePopover'
import validatePhoneNumber from 'shared/utils/helpers/validatePhoneNumber'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import CookieConsentBackdrop from 'shared/components/CookiesBackdrop'
import isAxiosError from 'shared/utils/helpers/isAxiosError'
import MuiPhone from 'shared/components/PhoneInput'

import { Form, FormikProvider, useFormik } from 'formik'
import { validateService } from './services/validatePhoneNumberService'
import { validateSmsCodeService } from './services/validateSmsCodeService'
import { getReflinkDataService } from './services/getReflinkDataService'
import { getSearchDataService } from './services/getSearchDataService'
import { confirmOrderService } from './services/confirmOrderService'
import { CustomerAddressesType, FormikDeliveryAddressType, ReflinkDataType, SearchResponseType } from './types'
import AddressData from './components/AddressData'
import SearchModal from './components/SearchModal'
import MainTable from './components/MainTable'
import { BoldTypography } from './styles'
import { de, en, pl } from './locales'
import { verifyAuthService } from './services/verifyAuthMethod'
import { getCustomerAddressesService } from './services/getCustomerAdressesService'
import { updateAddressService } from './services/updateAddressService'
import { addressSchema } from './components/AddressData/schema'

export default function ClientCartReflink() {
   const [data, setData] = useState<ReflinkDataType>()
   const [searchParams] = useSearchParams()
   const { t, i18n } = useTranslation(['TRANSLATION', 'CLIENT_CART'])
   i18n.addResourceBundle('pl-PL', 'CLIENT_CART', pl)
   i18n.addResourceBundle('en-EN', 'CLIENT_CART', en)
   i18n.addResourceBundle('de-DE', 'CLIENT_CART', de)
   const [phoneNumberValid, setPhoneNumberValid] = useState(false)
   const [smsValid, setSmsValid] = useState(false)
   const [phoneNumber, setPhoneNumber] = useState('')
   const [isAddressValid, setIsAddressValid] = useState(false)
   const [addresses, setAddresses] = useState<CustomerAddressesType>([])
   const [email, setEmail] = useState('')
   const [verificationMethod, setVerificationMethod] = useState<'PHONE' | 'EMAIL' | undefined>()
   const [smsCode, setSmsCode] = useState('')
   const theme = useTheme()
   const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
   const [searchValue, setSearchValue] = useState('')
   const [searchResponse, setSearchResponse] = useState<SearchResponseType>()
   const [isLoading, setIsLoading] = useState(false)
   const navigate = useNavigate()
   const { addNotification } = useNotificationContext()
   const [phoneNumberError, setPhoneNumberError] = useState(false)

   function handlePhoneNumber(value: string, country: string) {
      setPhoneNumber(value)
      const result = validatePhoneNumber(value, country)
      if (result === false) {
         setPhoneNumberError(true)
      } else {
         setPhoneNumberError(false)
      }
   }

   function validate() {
      if (phoneNumber || email) {
         const uuid = window.localStorage.getItem('uuid')
         if (uuid) {
            setIsLoading(true)
            if (verificationMethod === 'PHONE') {
               validateService(uuid, phoneNumber)
                  .then((res) => {
                     if (res.status === 204) {
                        addNotification(t('CLIENT_CART:verified'), 'success')
                        setPhoneNumberValid(true)
                     }
                  })
                  .catch((error) => {
                     if (isAxiosError<ErrorType>(error)) {
                        if (error.response?.data?.errors[0].code === 'errors.authentication.reflink-code-generation') {
                           addNotification(t('CLIENT_CART:phoneNumberVerifyFailed'), 'error')
                        } else {
                           addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
                        }
                     } else {
                        addNotification('failedSave', 'error')
                     }
                  })
                  .finally(() => setIsLoading(false))
            } else {
               validateService(uuid, undefined, email)
                  .then((res) => {
                     if (res.status === 204) {
                        addNotification(t('CLIENT_CART:verified'), 'success')
                        setPhoneNumberValid(true)
                     }
                  })
                  .catch((error) => {
                     if (isAxiosError<ErrorType>(error)) {
                        if (error.response?.data?.errors[0].code === 'errors.authentication.reflink-code-generation') {
                           addNotification(t('CLIENT_CART:phoneNumberVerifyFailed'), 'error')
                        } else {
                           addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
                        }
                     } else {
                        addNotification('failedSave', 'error')
                     }
                  })
                  .finally(() => setIsLoading(false))
            }
         }
      }
   }

   function submitSmsCode() {
      if (smsCode) {
         const token = window.localStorage.getItem('uuid')
         if (token) {
            setIsLoading(true)
            validateSmsCodeService(token, smsCode)
               .then((res) => {
                  if (res.status === 200) {
                     window.localStorage.setItem('reflink-verify-token', res.data)
                     addNotification(t('CLIENT_CART:verified'), 'success')
                     setSmsValid(true)
                  }
               })
               .catch((error) => {
                  if (isAxiosError<ErrorType>(error)) {
                     if (error.response?.data?.errors[0].code === 'errors.client-cart.verification-failed') {
                        addNotification(t('CLIENT_CART:smsFailed'), 'error')
                     } else {
                        addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
                     }
                  } else {
                     addNotification('failedSave', 'error')
                  }
               })
               .finally(() => setIsLoading(false))
         }
      }
   }

   function handleOnSearch() {
      const uuid = window.localStorage.getItem('uuid')
      const token = window.localStorage.getItem('reflink-verify-token')
      setIsLoading(true)
      if (uuid && token) {
         getSearchDataService(uuid, token, searchValue)
            .then((res) => {
               if (res.data.products.length > 0) {
                  setSearchResponse(res.data)
               } else {
                  addNotification(t('CLIENT_CART:notFoundProduct'), 'error')
               }
            })
            .catch((error) => {
               if (isAxiosError<ErrorType>(error)) {
                  if (
                     error.response?.data.errors[0].code === 'errors.company.is-blocked-due-downgrade' ||
                     error.response?.data.errors[0].code === 'errors.company.is-blocked-due-payment'
                  ) {
                     addNotification(t('CLIENT_CART:subscriptionError'), 'error')
                  } else if (error.response?.data.errors[0].code === 'errors.authentication.reflink-code-expired') {
                     addNotification(t('CLIENT_CART:expired'), 'error')
                     setSmsValid(false)
                     setPhoneNumberValid(false)
                     window.localStorage.removeItem('reflink-verify-token')
                  } else {
                     addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
                  }
               } else {
                  addNotification('failedSave', 'error')
               }
            })
            .finally(() => setIsLoading(false))
      }
   }
   const formik = useFormik<FormikDeliveryAddressType>({
      initialValues: {
         deliveryAddress: {
            street: '',
            houseNumber: '',
            zipCode: '',
            city: '',
            country: 'Poland',
         },
         extraComments: null,
         deliveryType: 'PICK_UP',
      },
      validationSchema: addressSchema,
      onSubmit: () => {},
   })

   const { values } = formik

   function handleOrderConfirm() {
      const uuid = window.localStorage.getItem('uuid')
      const token = window.localStorage.getItem('reflink-verify-token')
      setIsLoading(true)
      if (uuid && token) {
         updateAddressService(uuid, token, { ...values })
            .then((res) => {
               if (res) {
                  if (res.status === 200) {
                     addNotification('successSave', 'success')
                     setData(res.data)
                  }
               }
            })
            .catch((error) => {
               if (isAxiosError<ErrorType>(error)) {
                  addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
               } else {
                  addNotification('failedSave', 'error')
               }
            })
            .finally(() => {
               confirmOrderService(uuid, token)
                  .then((res) => {
                     if (res.status === 204) {
                        setTimeout(() => {
                           navigate(`/order-reflink-complete?uuid=${uuid}`)
                        }, 500)
                     }
                  })
                  .catch((error) => {
                     if (isAxiosError<ErrorType>(error)) {
                        addNotification(error.response?.data?.errors[0].code || t('CLEINT_CART:orderFailed'), 'error')
                     } else {
                        addNotification(t('CLEINT_CART:orderFailed'), 'error')
                     }
                  })
                  .finally(() => setIsLoading(false))
            })
      }
   }

   function renderCart() {
      const token = window.localStorage.getItem('reflink-verify-token')
      const uuid = window.localStorage.getItem('uuid')
      if (token && uuid) {
         if (data) {
            return (
               <Card
                  sx={{
                     display: 'flex',
                     flexDirection: 'column',
                     gap: { lg: 3, md: 2, xs: 1 },
                     p: 2,
                     width: { lg: '70%', md: '70%', xs: '100%' },
                     px: { lg: 15, md: 10, xs: 3 },
                     margin: '0 auto',
                     alignItems: 'center',
                  }}
               >
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 1,
                        mb: 1,
                        width: '100%',
                     }}
                  >
                     {data.companyReflinkAdditionalDetails.logoUrl && (
                        <img
                           height={isMobile ? '30px' : '70px'}
                           width="auto"
                           src={data.companyReflinkAdditionalDetails.logoUrl}
                           alt="company_logo"
                        />
                     )}
                     <BoldTypography variant="body1">{data.companyReflinkAdditionalDetails.companyName}</BoldTypography>
                     {data.companyReflinkAdditionalDetails.address && (
                        <BoldTypography>{data.companyReflinkAdditionalDetails.address}</BoldTypography>
                     )}
                     {data.companyReflinkAdditionalDetails.phoneNumber && (
                        <BoldTypography>{`Tel. ${data.companyReflinkAdditionalDetails.phoneNumber}`}</BoldTypography>
                     )}
                  </Box>
                  <OutlinedInput
                     fullWidth
                     autoFocus
                     onChange={(e) => setSearchValue(e.target.value)}
                     value={searchValue}
                     onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                           handleOnSearch()
                        }
                     }}
                     endAdornment={
                        <Button
                           onClick={handleOnSearch}
                           disabled={
                              isLoading ||
                              !searchValue ||
                              searchValue.replace(/[.,/#!$%^+&*;:{}=\-_<>@|\\[\]`~()'"\s?\\]/g, '').length < 2
                           }
                           variant="contained"
                           sx={{ height: 35 }}
                        >
                           <Box component={Search} sx={{ color: 'white', width: 20, height: 20 }} />
                        </Button>
                     }
                     startAdornment={
                        <InputAdornment position="start">
                           <Box component={Search} sx={{ color: 'text.disabled', width: 15, height: 15 }} />
                        </InputAdornment>
                     }
                     placeholder={t('TRANSLATION:searchPlaceholder')}
                  />
                  <MainTable
                     hideGrossPrices={data.hideGrossPrices}
                     orderValueNet={data.formattedNetClientPriceSum}
                     orderValueGross={data.formattedGrossClientPriceSum}
                     products={data.products}
                     updateData={(newData: ReflinkDataType) => setData(newData)}
                  />
                  <Box sx={{ width: '100%' }}>
                     <FormikProvider value={formik}>
                        <Form>
                           <AddressData
                              extraComments={data.extraComments}
                              availableAddresses={addresses}
                              locationCountry={data.locationCountry}
                              handleIsValid={(isValid: boolean) => setIsAddressValid(isValid)}
                              deliveryAddress={data.deliveryAddress}
                              deliveryType={data.deliveryType}
                           />
                        </Form>
                     </FormikProvider>
                  </Box>
                  <ContainedButton
                     sx={{ alignSelf: 'center', mt: { md: 0, xs: 1 } }}
                     onClick={handleOrderConfirm}
                     disabled={isLoading || data.products.length === 0 || !isAddressValid}
                     variant="contained"
                  >
                     {t('CLIENT_CART:confirmOrder')}
                  </ContainedButton>
               </Card>
            )
         }
      }
      return null
   }

   useEffect(() => {
      verifyAuthService(searchParams.get('uuid')!)
         .then((res) => {
            if (res.data.verificationMethod === 'SMS') {
               setVerificationMethod('PHONE')
            } else {
               setVerificationMethod('EMAIL')
            }
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               if (error.response?.data.errors[0].code === 'errors.client.not-found') {
                  addNotification(t('CLIENT_CART:clientNotFound'), 'error')
               } else if (error.response?.data.errors[0].code === 'errors.client-cart.not-found') {
                  addNotification(t('CLIENT_CART:cartRejected'), 'error')
               } else if (error.response?.data.errors[0].code === 'errors.authentication.reflink-code-expired') {
                  addNotification(t('CLIENT_CART:expired'), 'error')
                  setSmsValid(false)
                  setPhoneNumberValid(false)
                  window.localStorage.removeItem('reflink-verify-token')
               } else {
                  addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
               }
            } else {
               addNotification('failedSave', 'error')
            }
         })
   }, [])

   function renderContent() {
      if (!window.localStorage.getItem('reflink-verify-token')) {
         if (!phoneNumberValid && !smsValid) {
            return (
               <Box
                  sx={{
                     display: 'flex',
                     flexDirection: 'column',
                     gap: 3,
                     width: { lg: '40%', md: '70%', xs: '100%' },
                     margin: '0 auto',
                  }}
               >
                  <Typography variant="h4" color="primary">
                     {verificationMethod === 'EMAIL'
                        ? t('CLIENT_CART:emailVerify')
                        : t('CLIENT_CART:phoneNumberVerify')}
                  </Typography>
                  {verificationMethod ? (
                     <Box
                        sx={{ width: { lg: '40%', md: '70%', xs: '80%' }, margin: '0 auto' }}
                        onKeyDown={(e) => {
                           if (e.key === 'Enter') {
                              validate()
                           }
                        }}
                     >
                        {verificationMethod === 'PHONE' ? (
                           <MuiPhone
                              onChange={(value, country) => handlePhoneNumber(value, country)}
                              value={phoneNumber}
                              variant="outlined"
                           />
                        ) : (
                           <TextField
                              autoFocus
                              fullWidth
                              value={email}
                              onChange={(e) => setEmail(e.target.value.replace(/\s+/g, ''))}
                              label="E-mail"
                           />
                        )}
                        <ContainedButton
                           onClick={validate}
                           disabled={
                              (verificationMethod === 'PHONE' && (!phoneNumber || phoneNumberError)) ||
                              (verificationMethod === 'EMAIL' && !email) ||
                              isLoading
                           }
                           variant="contained"
                           sx={{ mt: 3 }}
                        >
                           {t('CLIENT_CART:confirm')}
                        </ContainedButton>
                     </Box>
                  ) : (
                     <CircularProgress sx={{ m: '0 auto' }} />
                  )}
               </Box>
            )
         }
         if (phoneNumberValid && !smsValid) {
            return (
               <Box
                  sx={{
                     display: 'flex',
                     flexDirection: 'column',
                     gap: 3,
                     width: { lg: '40%', md: '70%', xs: '100%' },
                     margin: '0 auto',
                  }}
               >
                  <Typography variant="h4" color="primary">
                     {verificationMethod === 'EMAIL'
                        ? t('CLIENT_CART:confirmEmailCode')
                        : t('CLIENT_CART:confirmSmsCode')}
                  </Typography>
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: { lg: '40%', md: '70%', xs: '80%' },
                        margin: '0 auto',
                     }}
                  >
                     <TextField
                        autoFocus
                        slotProps={{
                           htmlInput: {
                              maxLength: 6,
                           },
                        }}
                        onKeyDown={(e) => {
                           if (e.key === 'Enter') {
                              submitSmsCode()
                           }
                        }}
                        label={t('CLIENT_CART:verificationCode')}
                        value={smsCode}
                        onChange={(e) => setSmsCode(e.target.value)}
                     />
                     <ContainedButton
                        onClick={submitSmsCode}
                        disabled={!smsCode || smsCode.length < 6 || isLoading}
                        variant="contained"
                        sx={{ mt: 3 }}
                     >
                        {t('CLIENT_CART:confirm')}
                     </ContainedButton>
                  </Box>
               </Box>
            )
         }
      }
      return null
   }
   useEffect(() => {
      const reflinkUuid = searchParams.get('uuid')
      if (reflinkUuid) {
         if (reflinkUuid !== window.localStorage.getItem('uuid')) {
            setSmsValid(false)
            window.localStorage.removeItem('uuid')
            window.localStorage.removeItem('reflink-verify-token')
            window.localStorage.setItem('uuid', reflinkUuid)
            setPhoneNumberValid(false)
            renderContent()
         } else {
            window.localStorage.setItem('uuid', reflinkUuid)
            setPhoneNumberValid(false)
            setPhoneNumberValid(false)
         }
      } else {
         navigate('/')
      }
   }, [])

   useEffect(() => {}, [])

   useEffect(() => {
      const token = window.localStorage.getItem('reflink-verify-token')
      const uuid = window.localStorage.getItem('uuid')
      if (token && uuid) {
         getReflinkDataService(uuid, token)
            .then((res) => {
               setData(res.data)
            })
            .catch((error) => {
               if (isAxiosError<ErrorType>(error)) {
                  if (error.response?.data.errors[0].code === 'errors.client-cart.not-found') {
                     addNotification(t('CLIENT_CART:cartRejected'), 'error')
                  }
                  if (error.response?.data.errors[0].code === 'errors.authentication.reflink-code-expired') {
                     addNotification(t('CLIENT_CART:expired'), 'error')
                     setSmsValid(false)
                     setPhoneNumberValid(false)
                     window.localStorage.removeItem('reflink-verify-token')
                  }
                  addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
               } else {
                  addNotification('failedSave', 'error')
               }
            })
      }
   }, [window.localStorage.getItem('reflink-verify-token')])

   useEffect(() => {
      const token = window.localStorage.getItem('reflink-verify-token')
      const uuid = window.localStorage.getItem('uuid')
      if (token && uuid) {
         getCustomerAddressesService(uuid, token)
            .then((res) => {
               setAddresses(res.data)
            })
            .catch((error) => {
               if (isAxiosError<ErrorType>(error)) {
                  if (error.response?.data.errors[0].code === 'errors.client-cart.not-found') {
                     addNotification(t('CLIENT_CART:cartRejected'), 'error')
                  }
                  if (error.response?.data.errors[0].code === 'errors.authentication.reflink-code-expired') {
                     addNotification(t('CLIENT_CART:expired'), 'error')
                     setSmsValid(false)
                     setPhoneNumberValid(false)
                     window.localStorage.removeItem('reflink-verify-token')
                  }
                  addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
               } else {
                  addNotification('failedSave', 'error')
               }
            })
      }
   }, [window.localStorage.getItem('reflink-verify-token')])

   return (
      <Box sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
         <Container sx={{ zIndex: 40, minWidth: '100%', display: 'flex', justifyContent: 'flex-end', pt: 5 }}>
            <LanguagePopover />
         </Container>
         <Box
            sx={{
               display: 'flex',
               flexDirection: 'column',
               gap: 3,
               paddingTop: '5%',
               minHeight: 'calc(100vh - 199px)',
               textAlign: 'center',
            }}
         >
            {window.localStorage.getItem('reflink-verify-token') ? renderCart() : renderContent()}
         </Box>
         {data && (
            <SearchModal
               hideGrossPrices={data.hideGrossPrices}
               open={!!searchResponse}
               data={searchResponse}
               handleClose={() => {
                  setSearchValue('')
                  setSearchResponse(undefined)
               }}
               searchPhrase={searchValue}
               updateData={(newData: ReflinkDataType) => setData(newData)}
            />
         )}
         <CookieConsentBackdrop />
      </Box>
   )
}
